'use client';

import { useEffect, useRef, useState } from 'react';
import { useLoadedWebpSource } from '@/components/tools/webp-tool-shared';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import { convertWebpToGif, formatFileSize, getBaseName } from '@/lib/browser-webp-tools';

type FpsCapOption = 'keep' | '30' | '20' | '12';
type LoopMode = 'keep' | 'infinite' | 'once';

export default function BrowserWebpToGif() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadSource, clearSource } = useLoadedWebpSource();
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [targetWidth, setTargetWidth] = useState(640);
  const [fpsCap, setFpsCap] = useState<FpsCapOption>('keep');
  const [loopMode, setLoopMode] = useState<LoopMode>('keep');
  const [qualityPreset, setQualityPreset] = useState<'high' | 'balanced' | 'smaller'>('balanced');

  useEffect(() => {
    return () => {
      if (outputUrl) {
        URL.revokeObjectURL(outputUrl);
      }
    };
  }, [outputUrl]);

  useEffect(() => {
    if (!source) {
      return;
    }

    setTargetWidth(source.metadata.width);
  }, [source]);

  function resetOutputState() {
    if (outputUrl) {
      URL.revokeObjectURL(outputUrl);
    }

    setOutputUrl(null);
    setDownloadName(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(error instanceof Error ? error.message : 'The WebP could not be loaded.');
    }
  }

  async function runConversion() {
    if (!source) {
      setErrorMessage('Choose an animated WebP first.');
      return;
    }

    if (!source.metadata.animated) {
      setErrorMessage('This WebP is static. Use Image Converter if you need a still GIF.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    const outputLoopCount =
      loopMode === 'keep' ? source.metadata.loopCount : loopMode === 'infinite' ? 0 : 1;

    try {
      const credits = await consumeToolUsage(
        'webp-to-gif',
        'Credits are not available for another WebP to GIF conversion right now.',
      );
      const outputBlob = await convertWebpToGif({
        file: source.file,
        width: targetWidth,
        fpsCap: fpsCap === 'keep' ? null : Number(fpsCap),
        loopCount: outputLoopCount,
        qualityPreset,
      });
      const nextOutputUrl = URL.createObjectURL(outputBlob);

      resetOutputState();
      setOutputUrl(nextOutputUrl);
      setDownloadName(`${getBaseName(source.file.name)}.gif`);
      setResultSize(outputBlob.size);
      setCreditMessage(formatToolCreditSuccess('WebP to GIF conversion', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The WebP could not be converted to GIF.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Convert animated WebP to GIF</h2>
          <p>
            Export animated WebP loops as GIF for legacy embeds, docs, and older sharing surfaces
            that still expect GIF.
          </p>
        </div>
        <span className="tool-live-badge">GIF</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {source ? source.file.name : 'Choose one animated WebP or drop it here'}
              </strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${source.metadata.width} × ${source.metadata.height}`
                  : 'Animated WebP only'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/webp,.webp"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Output width</span>
              <input
                min="2"
                onChange={(event) => {
                  setTargetWidth(Math.max(2, Number(event.target.value) || 2));
                  resetOutputState();
                }}
                type="number"
                value={targetWidth}
              />
            </label>
            <label className="tool-live-field">
              <span>FPS cap</span>
              <select
                onChange={(event) => {
                  setFpsCap(event.target.value as FpsCapOption);
                  resetOutputState();
                }}
                value={fpsCap}
              >
                <option value="keep">Keep</option>
                <option value="30">30 fps</option>
                <option value="20">20 fps</option>
                <option value="12">12 fps</option>
              </select>
            </label>
          </div>

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Loop mode</span>
              <select
                onChange={(event) => {
                  setLoopMode(event.target.value as LoopMode);
                  resetOutputState();
                }}
                value={loopMode}
              >
                <option value="keep">Keep source</option>
                <option value="infinite">Infinite</option>
                <option value="once">Play once</option>
              </select>
            </label>
            <label className="tool-live-field">
              <span>Quality</span>
              <select
                onChange={(event) => {
                  setQualityPreset(event.target.value as 'high' | 'balanced' | 'smaller');
                  resetOutputState();
                }}
                value={qualityPreset}
              >
                <option value="high">High quality</option>
                <option value="balanced">Balanced</option>
                <option value="smaller">Smaller file</option>
              </select>
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing}
              onClick={() => void runConversion()}
              type="button"
            >
              {isProcessing ? 'Converting…' : 'Convert to GIF'}
            </button>
            {outputUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={outputUrl}>
                Download GIF
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Conversion details</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Frames</strong>
              <span>{source?.metadata.frameCount ?? 'No file loaded'}</span>
            </div>
            <div>
              <strong>Duration</strong>
              <span>
                {source?.metadata.animated
                  ? `${(source.metadata.durationMs / 1000).toFixed(2)}s`
                  : 'Static'}
              </span>
            </div>
            <div>
              <strong>Output size</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Not exported yet'}</span>
            </div>
            <div>
              <strong>Compatibility</strong>
              <span>GIF is easier for legacy embeds and docs</span>
            </div>
          </div>
        </aside>
      </div>
    </article>
  );
}
